import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import { colors, fonts, space } from '../../tokens'

// Legacy's audio speed button cycles through these
export const PLAYBACK_RATES = [1, 1.3, 1.5, 1.7, 2]

export const nextPlaybackRate = (rate: number) =>
  PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(rate) + 1) % PLAYBACK_RATES.length]

// 0:07, 36:12, 1:02:09
export const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const whole = Math.floor(seconds)
  const hours = Math.floor(whole / 3600)
  const minutes = Math.floor((whole % 3600) / 60)
  const pad = (n: number) => String(n).padStart(2, '0')
  const rest = `${pad(whole % 60)}`
  return hours ? `${hours}:${pad(minutes)}:${rest}` : `${minutes}:${rest}`
}

// A plain "x", as in the prototype: it sits on the baseline with the digits,
// where the multiplication sign floats at the midline
const formatRate = (rate: number) => `${rate}x`

const ICON = 18

const PlayIcon = () => (
  <svg width={ICON} height={ICON} viewBox="0 0 24 24" aria-hidden>
    <path d="M7 4.5v15l12.5-7.5z" fill={colors.text} />
  </svg>
)

const PauseIcon = () => (
  <svg width={ICON} height={ICON} viewBox="0 0 24 24" aria-hidden>
    <path d="M6 4.5h4v15H6zm8 0h4v15h-4z" fill={colors.text} />
  </svg>
)

const SpeakerIcon = ({ muted }: { muted: boolean }) => (
  <svg width={ICON} height={ICON} viewBox="0 0 24 24" aria-hidden>
    <path d="M4 9.5h4L13 5v14l-5-4.5H4z" fill={colors.text} />
    {muted ? (
      <path
        d="M16 9.5l5 5m0-5l-5 5"
        stroke={colors.text}
        strokeWidth={2}
        strokeLinecap="round"
      />
    ) : (
      <path
        d="M16 9a4 4 0 010 6m2.5-8.5a7.5 7.5 0 010 11"
        stroke={colors.text}
        strokeWidth={2}
        strokeLinecap="round"
        fill="none"
      />
    )}
  </svg>
)

const ControlButton = ({
  label,
  onPress,
  children
}: {
  label: string
  onPress: () => void
  children: ReactNode
}) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={label}
    onPress={onPress}
    style={{
      width: 32,
      height: 32,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 16
    }}
  >
    {children}
  </Pressable>
)

// The unplayed track: a mid gray (between --border-default and
// --text-tertiary) that stands out from the pill, as in the prototype
const TRACK_COLOR = '#BDBCBA'

// Width of the browser's own range handle (Chrome and Safari draw ~16px)
const SLIDER_HANDLE = 16

const fill = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%'
}

/**
 * A thin track with the played part filled in, as in the prototype. A
 * native range input lies invisibly on top, so dragging, the keyboard and
 * screen readers work as for any slider; a dot marks the position while it's
 * hovered, focused or dragged.
 */
const SeekBar = ({
  time,
  duration,
  onSeek
}: {
  time: number
  duration: number
  onSeek: (value: number) => void
}) => {
  const [active, setActive] = useState(false)
  const fraction = duration ? Math.min(time / duration, 1) : 0
  const position = `${fraction * 100}%`

  return (
    <View
      style={{
        flex: 1,
        minWidth: 40,
        height: 32,
        marginHorizontal: space[2],
        justifyContent: 'center'
      }}
    >
      <View
        style={{
          height: 4,
          borderRadius: 2,
          backgroundColor: TRACK_COLOR,
          overflow: 'hidden'
        }}
      >
        <View
          style={{
            width: position,
            height: '100%',
            backgroundColor: colors.text
          }}
        />
      </View>
      {active && !!duration && (
        <View
          pointerEvents="none"
          style={{
            position: 'absolute',
            left: position,
            top: '50%',
            width: 10,
            height: 10,
            marginLeft: -5,
            marginTop: -5,
            borderRadius: 5,
            backgroundColor: colors.text
          }}
        />
      )}
      <input
        type="range"
        min={0}
        max={duration || 0}
        step="any"
        value={Math.min(time, duration || 0)}
        disabled={!duration}
        onChange={e => onSeek(Number(e.target.value))}
        onMouseEnter={() => setActive(true)}
        onMouseLeave={() => setActive(false)}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
        aria-label="Seek"
        aria-valuetext={`${formatTime(time)} of ${formatTime(duration)}`}
        // Wider than the drawn track by half the browser's handle (about 16px)
        // at each end: the slider maps its range between the handle's centres,
        // so this lines the range up with the track the user sees
        style={{
          ...fill,
          left: -SLIDER_HANDLE / 2,
          width: `calc(100% + ${SLIDER_HANDLE}px)`,
          margin: 0,
          opacity: 0,
          cursor: duration ? 'pointer' : 'default'
        }}
      />
    </View>
  )
}

const controlText = {
  fontFamily: fonts.sans,
  fontSize: 13,
  color: colors.text
}

/**
 * Our own audio player, in place of the browser's controls: play / pause,
 * elapsed and total time, a seek bar, mute, and legacy's speed button, in one
 * gray pill (the prototype's design). It looks the same in every browser and
 * has no browser menu, so no download option. The seek bar is a native range
 * input, so it works with the keyboard and screen readers.
 */
const AudioPlayer = ({ src }: { src: string }) => {
  const audio = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [muted, setMuted] = useState(false)
  const [rate, setRate] = useState(1)

  // A new file starts over (the element keeps no state across sources)
  useEffect(() => {
    setPlaying(false)
    setTime(0)
    setDuration(0)
  }, [src])

  const togglePlay = () => {
    const element = audio.current
    if (!element) return
    if (element.paused) element.play().catch(() => {})
    else element.pause()
  }

  const seek = (value: number) => {
    if (audio.current) audio.current.currentTime = value
    setTime(value)
  }

  const toggleMute = () => {
    if (audio.current) audio.current.muted = !audio.current.muted
  }

  const changeRate = () => {
    const next = nextPlaybackRate(rate)
    if (audio.current) {
      audio.current.playbackRate = next
      // Survives the element reloading its source
      audio.current.defaultPlaybackRate = next
    }
    setRate(next)
  }

  const readDuration = (element: HTMLAudioElement) =>
    setDuration(Number.isFinite(element.duration) ? element.duration : 0)

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        width: '100%',
        maxWidth: 480,
        height: 48,
        paddingHorizontal: space[2],
        borderRadius: 24,
        backgroundColor: colors.band
      }}
    >
      <audio
        ref={audio}
        src={src}
        preload="metadata"
        onLoadedMetadata={e => readDuration(e.currentTarget)}
        onDurationChange={e => readDuration(e.currentTarget)}
        onTimeUpdate={e => setTime(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onVolumeChange={e => setMuted(e.currentTarget.muted)}
        style={{ display: 'none' }}
      />
      <ControlButton label={playing ? 'Pause' : 'Play'} onPress={togglePlay}>
        {playing ? <PauseIcon /> : <PlayIcon />}
      </ControlButton>
      <Text
        style={{
          ...controlText,
          marginHorizontal: space[2],
          // Digits keep their width, so the bar doesn't jiggle as time passes
          fontVariant: ['tabular-nums']
        }}
      >
        {formatTime(time)} / {formatTime(duration)}
      </Text>
      <SeekBar time={time} duration={duration} onSeek={seek} />
      <ControlButton label={muted ? 'Unmute' : 'Mute'} onPress={toggleMute}>
        <SpeakerIcon muted={muted} />
      </ControlButton>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Playback speed ${formatRate(rate)}`}
        onPress={changeRate}
        style={{ minWidth: 40, height: 32, justifyContent: 'center' }}
      >
        <Text
          style={{ ...controlText, fontWeight: '600', textAlign: 'center' }}
        >
          {formatRate(rate)}
        </Text>
      </Pressable>
    </View>
  )
}

export default AudioPlayer
